import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input } from '@angular/core';
import { FeatureActionsMap, IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import { DeltaService } from '@omni/data-services/delta/delta.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { CallPlanOfflineService, CallPlanSummary } from '@omni/services/call-plan/call-plan.offline.service';
import { EventsService } from '@omni/services/events/events.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { ComponentViewMode, UIService } from '@omni/services/ui/ui.service';
import { debounceTime, filter, merge, Subject, Subscription, takeUntil } from 'rxjs';
import { OpportunityManagementService } from '@omni/services/opportunity-management/opportunity-management.service';
import { DeviceService } from '@omni/services/device/device.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { ReportDataManagementService } from '@omni/services/reports/report-data-management.service';
import { CommonDimensions, MeasureType } from '@omni/enums/edge-analytics/edge-analytics.enum';
import { ReportService } from '@omni/services/reports/report.service';
import { TranslateService } from '@ngx-translate/core';
import { TrackingEventNames, TrackService } from '@omni/services/logging/tracking.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { BaseMeasureData } from 'src/interfaces/edge-analytics/report.interface';
import { MessageMeasureData } from 'src/interfaces/edge-analytics/message-report.interface';
import { MessageReportYearMonthKeyProp } from '@omni/config/edge-analytics/message-report/message-report.config';
import { MeetingMeasureData } from 'src/interfaces/edge-analytics/meeting-report.interface';
import { CoachingMeasureData } from 'src/interfaces/edge-analytics/coaching-report.interface';
import { SurgeryOrderMeasureData } from 'src/interfaces/edge-analytics/surgery-order-report.interface';
import { CommonModule } from '@angular/common';
import { AreaGraphComponent } from './graphs/area-graph-component';
import { LineGraphComponent } from './graphs/line-graph-component';
import { BarGraphComponent } from './graphs/bar-graph-component';
import { CircularChartComponent } from './graphs/circular-chart-component';
import { IonicModule } from '@ionic/angular';
import { MapPipe } from '@omni/pipes/map.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { MeetingDimensions } from '@omni/enums/edge-analytics/meeting/meeting-report.enum';
import { MessageDimensions } from '@omni/enums/edge-analytics/message/message-report.enum';
import { CoachingReportYearMonthKeyProp } from '@omni/config/edge-analytics/coaching-report/coaching-report.config';
import { MeetingReportYearMonthKeyProp } from '@omni/config/edge-analytics/meeting-report/meeting-report.config';
import { CoachingDimensions } from '@omni/enums/edge-analytics/coaching/coaching-report.enum';
import { SurgeryOrderActivity } from '@omni/classes/activity/surgery-order.activity.class';
import { _getAvgCoachingScoreDiceFilter } from '@omni/services/reports/functions/coaching/coaching-report.functions';
import { isEqual, keyBy, range, sumBy, uniqBy } from 'lodash-es';
import { _ } from '@omni/utility/lodash-chain';
interface Metric {
  tool: string;
  label: string;
  subtext: string;
  value: number;
  data?: { value1?: number; value2?: number, label: string }[];
  count: number;
  stringValue: string;
  isLoading: boolean;
  isRefreshing?: boolean;
  isSelected?: boolean;
  callPlanSummary?: CallPlanSummary;
}

function getYearMonth(offset: number): number {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - offset);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return year * 100 + month;
}

@Component({
  selector: 'go-app-metrics',
  templateUrl: './go-app-metrics.component.html',
  styleUrl: './go-app-metrics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    MapPipe,
    TranslateModule,
    AreaGraphComponent,
    LineGraphComponent,
    BarGraphComponent,
    CircularChartComponent,
  ]
})
export class GoAppMetricsComponent {

  @Input() public __source = '';
  public metrics: Metric[] = [];
  private _measures: Map<MeasureType, BaseMeasureData> = new Map();

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly syncCompleted$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  MeasureType = MeasureType;
  private last4Months: string[] = [];
  private _allMonths = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'].map(month => this.translate.instant(month) as string);
  private _edgeAnalyticsDataCubeUserId: number;
  private _edgeAnalyticsDataChangeSubscriptions: Subscription[] = [];
  private readonly edgeLoaderMeasureTypes: MeasureType[] = [
    MeasureType.meeting,
    MeasureType.message,
    MeasureType.coaching,
  ];
  private refreshingEdgeMeasures: MeasureType[] = [];

  constructor(
    private readonly authService: AuthenticationService,
    private readonly callPlanOfflineService: CallPlanOfflineService,
    private readonly events: EventsService,
    private readonly deltaService: DeltaService,
    private readonly opportunityService: OpportunityManagementService,
    private readonly navService: NavigationService,
    private readonly device: DeviceService,
    private readonly dateTimeFormat: DateTimeFormatsService,
    private readonly accountService: AccountOfflineService,
    private readonly cdr: ChangeDetectorRef,
    private readonly reportService: ReportService,
    public readonly translate: TranslateService,
    private readonly reportDataMgmService: ReportDataManagementService,
    private readonly uiService: UIService,
    private readonly trackService: TrackService,
    private readonly notificationService: NotificationService,
    private readonly el: ElementRef<HTMLDivElement>,
  ) {
    this.initMonths();
  }

  private initMonths() {
    const currentMonth = new Date().getMonth();
    const last4MonthIndexes = [];
    for (let i = 3; i >= 0; i--) {
      const index = (currentMonth - i + 12) % 12;
      last4MonthIndexes.push(index);
    }

    this.last4Months = last4MonthIndexes.map(i => this._allMonths[i]);
  }

  private updateTranslatedMonths() {
    this._allMonths = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'].map(month => this.translate.instant(month) as string);
  }

  public async ngOnInit() {

    this.translate.onLangChange.subscribe(data => {
      // Update month labels when language changes
      this.updateTranslatedMonths();
      this.initMonths();
      
      // Update metric labels when language changes
      this.metrics.forEach(metric => {
        switch (metric.tool) {
          case 'call_plan':
            metric.label = this.translate.instant('CALL_PLANS');
            metric.subtext = this.translate.instant('CALL_PLAN_GOAL_COMPLETION');
            break;
          case 'opportunity':
            metric.label = this.translate.instant('OPPORTUNITIES');
            metric.subtext = this.translate.instant('ESTIMATED_VALUE');
            break;
          case MeasureType.meeting:
            metric.label = this.translate.instant('MEETINGS') + ' & ' + this.translate.instant('PHONE_CALLS');
            metric.subtext = this.translate.instant('COMPLETED');
            break;
          case MeasureType.message:
            metric.label = this.translate.instant('MESSAGES');
            metric.subtext = this.translate.instant('TOTAL_SENT');
            break;
          case MeasureType.coaching:
            metric.label = this.translate.instant('COACHINGS');
            metric.subtext = this.translate.instant('AVERAGE_COACHING_SCORE');
            break;
          case MeasureType.procedure:
            metric.label = this.translate.instant('PROCEDURES');
            metric.subtext = this.translate.instant('TOTAL');
            break;
          case MeasureType.salesOrder:
            metric.label = this.translate.instant('SALES_DATA');
            metric.subtext = this.translate.instant('ACTUAL_TARGET');
            break;
        }
      });
      
      // Update data labels for metrics with trend data
      this.metrics.forEach(metric => {
        if (metric.data) {
          metric.data = metric.data.map(item => ({
            ...item,
            label: this._allMonths[this.last4Months.indexOf(item.label)]
          }));
        }
      });
      
      this.cdr.markForCheck();
    });

    // Move subscription logic from constructor to ngOnInit
    merge(
      this.reportDataMgmService.procedureLogDataReadyToBeLoaded$.pipe(filter(ready => ready)),
      this.events.observe('surgeryOrderActivityCompleted'),
      this.events.observe('surgeryOrderActivityDeleted'),
      this.events.observe('surgeryOrderActivityReopened')
    )
    .pipe(
      debounceTime(100),
      takeUntil(this.destroy$))
    .subscribe(async () => {
      this.reportDataMgmService.resetLoadingCounter();
      this.reportDataMgmService.requestLocalDataRefresh(MeasureType.procedure);
    });

    this.reportDataMgmService.selectedMeasure$.pipe(takeUntil(this.destroy$)).subscribe(selected => {
      this.el.nativeElement.closest('go-app-activities-page')?.classList?.toggle('metric-selected', selected !== null);
      if (selected === null) {
        // Closed the detail page
        const curSelectedMeasureCard = this.metrics.find(m => m.isSelected);
        if (curSelectedMeasureCard) {
          curSelectedMeasureCard.isSelected = false;
        }
        this.cdr.markForCheck();
      }
    });
    this.reportDataMgmService.edgeMetricsRefreshingMeasures$
      .pipe(takeUntil(this.destroy$))
      .subscribe((syncingEdgeMeasures) => {
        this.refreshingEdgeMeasures = syncingEdgeMeasures;
        this.setEdgeMetricsRefreshState(this.refreshingEdgeMeasures);
      });

    await this.initMetrics();
    this.subscriptions.push(
      this.deltaService.masterSyncState.stateObservable
        .subscribe(
          (masterSyncState => {
            if (masterSyncState === 'Sync') {
              this.initMetrics();
            }
            if (masterSyncState === 'OfflineDataPushOnly') {
              this.initMetrics(true);
            }
          })
        )
    );
    if (this.__source == 'dashboard') {
      await this.updateCallPlanMetric();
      this.updateOpportunityMetric(this.opportunityService.opportunities || []);
    }
    this.syncCompleted$
      .pipe(
        debounceTime(200),
        takeUntil(this.destroy$))
      .subscribe(async () => {
      if (this.device.isOffline) {
        await this.updateCallPlanMetric();
      }
      this.updateEdgeAnalyticsMeasureCards();
    });
    this.events.observe('device:deviceIsOffline').pipe(takeUntil(this.destroy$)).subscribe((status) => {
      this.setEdgeMetricsRefreshState([]);
      this.initMetrics();
    });
    this.events.observe('syncCallPlanActivities:Initiated').pipe(takeUntil(this.destroy$)).subscribe(async () => {
      let callPlanMetric = this.metrics.find(m => m.tool === 'call_plan');
      if (callPlanMetric) {
        callPlanMetric.isLoading = true;
      }
    });

    this.events.observe('callPlanActivitiesUpdated').pipe(takeUntil(this.destroy$)).subscribe(async () => {
      await this.updateCallPlanMetric();
    });
    this.opportunityService.opportunities$.pipe(takeUntil(this.destroy$)).subscribe((opportunities) => {
      try {
        if (opportunities?.length) {
          this.updateOpportunityMetric(opportunities || []);
          this.cdr.markForCheck();
        } else {
          let opportunityMetric = this.metrics.find(m => m.tool === 'opportunity');
          if (opportunityMetric) {
            opportunityMetric.stringValue = this.dateTimeFormat.symbolPosSelected.value;
            opportunityMetric.value = 0;
            opportunityMetric.count = 0;
            opportunityMetric.isLoading = false;
            this.cdr.markForCheck();
          }
        }
      } catch (error) {
        console.error('Error updating opportunity metric:', error);
      }
    });
    this.reportDataMgmService.measures$.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(measures => {
      for (const [measureType, measure] of measures) {
        this._measures.set(measureType, measure);
      }
      this.updateEdgeAnalyticsMeasureCards();
    });
  }

  @HostListener('window:omni:sync:completed', ['$event'])
  @HostListener('window:omni:offlineDataLoad:Completed', ['$event'])
  syncCompleted(_event?: Event) {
    this.syncCompleted$.next();
  }

  private getEnabledMetrics() {
    const callPlanEnabled = this.authService.hasFeatureAction(FeatureActionsMap.CALL_PLANS);
    const opportunityEnabled = this.authService.hasFeatureAction(FeatureActionsMap.OPPORTUNITY_MANAGEMENT);
    const edgeAnalyticsEnabled = this.authService.hasFeatureAction(FeatureActionsMap.EDGE_ANALYTICS_MEETING);
    const meetingEnabled = edgeAnalyticsEnabled && this.authService.hasFeatureAction(FeatureActionsMap.BASELINE);
    const messageEnabled = edgeAnalyticsEnabled && this.authService.hasFeatureAction(FeatureActionsMap.MESSAGE_ACTIVITY);
    // Coaching KPI temporarily hidden from the homepage metrics.
    // const coachingEnabled = edgeAnalyticsEnabled && this.authService.hasFeatureAction(FeatureActionsMap.COACHING_TOOL);
    const procedureEnabled = this.authService.user.hasProcedureLog;
    const salesOrderEnabled = this.authService.hasFeatureAction(FeatureActionsMap.SALES_DATA_REPORT);
    let metrics: Metric[] = [];

    if (callPlanEnabled) {
      metrics.push({
        tool: 'call_plan',
        label: this.translate.instant('CALL_PLANS'),
        subtext: this.translate.instant('CALL_PLAN_GOAL_COMPLETION'),
        value: 0,
        count: 0, 
        stringValue: '0%',
        isLoading: true,
      });
    }
    if (opportunityEnabled) {
      metrics.push({
        tool: 'opportunity',
        label: this.translate.instant('OPPORTUNITIES'),
        subtext: this.translate.instant('ESTIMATED_VALUE'),
        value: 0,
        count: 0,
        stringValue: this.dateTimeFormat.symbolPosSelected.value,
        isLoading: true,
      });
    }
    if (meetingEnabled) {
      metrics.push({
        tool: MeasureType.meeting,
        label: this.translate.instant('MEETINGS') + ' & ' + this.translate.instant('PHONE_CALLS'),
        subtext: this.translate.instant('COMPLETED'),
        value: 0,
        count: 0,
        stringValue: '',
        isLoading: true,
        isRefreshing: false,
        data: this.last4Months.map(month => ({ value1: 0, label: month })),
      });
    }
    if (messageEnabled) {
      metrics.push({
        tool: MeasureType.message,
        label: this.translate.instant('MESSAGES'),
        subtext: this.translate.instant('TOTAL_SENT'),
        value: 0,
        count: 0,
        stringValue: '0%',
        isLoading: true,
        isRefreshing: false,
        data: this.last4Months.map(month => ({ value1: 0, value2: 0, label: month })),
      });
    }
    // TODO: Coaching KPI temporarily hidden from the homepage metrics.
    // if (coachingEnabled) {
    //   metrics.push({
    //     tool: MeasureType.coaching,
    //     label: this.translate.instant('COACHINGS'),
    //     subtext: this.translate.instant('AVERAGE_COACHING_SCORE'),
    //     value: 0,
    //     count: 0,
    //     stringValue: '0%',
    //     isLoading: true,
    //     isRefreshing: false,
    //     data: this.last4Months.map(month => ({ value1: 0, label: month })),
    //   });
    // }
    if (procedureEnabled) {
      metrics.push({
        tool: MeasureType.procedure,
        label: this.translate.instant('PROCEDURES'),
        subtext: this.translate.instant('TOTAL'),
        value: 0,
        count: 0,
        stringValue: '',
        isLoading: true,
        data: this.last4Months.map(month => ({ value1: 0, value2: 0, label: month })),
      });
    }
    if (salesOrderEnabled) {
      metrics.push({
        tool: MeasureType.salesOrder,
        label: this.translate.instant('SALES_DATA'),
        subtext: this.translate.instant('ACTUAL_TARGET'),
        value: 0,
        count: 0,
        stringValue: '0',
        isLoading: true,
      });
    }
    return metrics;
  }

  @HostListener('window:omni:user:updated')
  async onUserUpdated() {
    const enabledMetrics = this.getEnabledMetrics().map(metric => metric.tool);
    const currentMetrics = this.metrics.map(metric => metric.tool);
    const hasChanges = !isEqual(enabledMetrics, currentMetrics);
    if (hasChanges) {
      await this.initMetrics();
    }
  }

  private async initMetrics(fetchOfflineData: boolean = false): Promise<void> {
    this.metrics = this.getEnabledMetrics()
      .map(metric => {
        let existingMetric = this.metrics.find(m => m.tool === metric.tool);
        if (existingMetric) {
          return existingMetric;
        }
        return metric;
      });
    if (this.device.isOffline || fetchOfflineData) {
      for (const metric of this.metrics) {
        if (metric.tool === 'call_plan') {
          await this.updateCallPlanMetric();
        }
        if (metric.tool === 'opportunity') {
          this.updateOpportunityMetric(this.opportunityService.opportunities || []);
        }
        }
      this._measures = new Map(_(this.metrics)
        .filter(metric => metric.tool !== "call_plan" && metric.tool !== "opportunity")
        .map(m => [m.tool, this.reportDataMgmService.getMeasure(m.tool as MeasureType)] as [MeasureType, BaseMeasureData])
        .filter(m => !!m[1])
        .value());

      if (this._measures.size) {
        this.updateEdgeAnalyticsMeasureCards();
      }
    }
    this.setEdgeMetricsRefreshState(this.refreshingEdgeMeasures);
    this.cdr.markForCheck();
  }

  public shouldShowSkeleton(metric: Metric): boolean {
    return metric.isLoading && !metric.isRefreshing;
  }

  public isMetricDisabled(metric: Metric): boolean {
    return !!metric.isRefreshing;
  }

  private setEdgeMetricsRefreshState(syncingEdgeMeasures: MeasureType[]): void {
    const syncingMeasuresSet = new Set(syncingEdgeMeasures);
    for (const measureType of this.edgeLoaderMeasureTypes) {
      const metric = this.metrics.find(m => m.tool === measureType);
      if (!metric) {
        continue;
      }
      const isRefreshing = !this.device.isOffline && syncingMeasuresSet.has(measureType);
      if (!isRefreshing) {
        metric.isRefreshing = false;
        continue;
      }

      // Delta sync: show overlay only when old measure data exists.
      // Initial sync: keep skeleton until first measure is available.
      const hasOldMeasureData = this.reportDataMgmService.hasMeasure(measureType);
      if (hasOldMeasureData) {
        metric.isRefreshing = true;
        metric.isLoading = false;
      } else {
        metric.isRefreshing = false;
        metric.isLoading = true;
      }
    }
    this.cdr.markForCheck();
  }

  private async updateCallPlanMetric(): Promise<void> {
    let callPlanMetric = this.metrics.find(m => m.tool === 'call_plan');
    if (!callPlanMetric) {
      console.error('Call plan metric not found');
      return;
    }
    try {
      callPlanMetric.isLoading = true;
      this.cdr.markForCheck();
      const currentCallPlans = await this.callPlanOfflineService.presentCallPlans();
      const summary = this.callPlanOfflineService.summarizeCallPlans(currentCallPlans);
      callPlanMetric.callPlanSummary = summary;
      callPlanMetric.value = summary.progressPercentage;
      callPlanMetric.count = currentCallPlans.length;
      callPlanMetric.stringValue = summary.progressPercentage + '%';
      callPlanMetric.isLoading = false;
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error updating call plan metric:', error);
      callPlanMetric.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private updateOpportunityMetric(opportunities: any[]): void {
    let opportunityMetric = this.metrics.find(m => m.tool === 'opportunity');
    if (!opportunityMetric) {
      console.error('Opportunity metric not found');
      return;
    }
    try {
      opportunityMetric.isLoading = true;
      this.cdr.markForCheck();
      const openOpportunities = opportunities.filter(opp => opp.stateCode === 0 && this.accountService.getAccountById(opp.accountID));
      if (openOpportunities.length === 0) {
        opportunityMetric.stringValue = this.dateTimeFormat.symbolPosSelected.value;
        opportunityMetric.value = 0;
        opportunityMetric.count = 0;
      }

      if (openOpportunities.length !== 0) {
        let totalEstimatedValue = 0;
        let currencySymbol;

        openOpportunities.forEach(opp => {
          if (opp.estimatedValue) {
            totalEstimatedValue += opp.estimatedValue;
          }
          if (opp.transactionCurrencySymbol && !currencySymbol) {
            currencySymbol = opp.transactionCurrencySymbol;
          }
        });

        totalEstimatedValue = Math.round(totalEstimatedValue);
        const formattedValue = this.formatEstimatedValue(totalEstimatedValue);
        opportunityMetric.value = totalEstimatedValue;
        opportunityMetric.count = openOpportunities.length;
        opportunityMetric.stringValue = currencySymbol + formattedValue;
      }
      opportunityMetric.isLoading = false;
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error updating opportunity metric:', error);
      opportunityMetric.isLoading = false;
      this.cdr.markForCheck();
    }
  }


  private updateEdgeAnalyticsMeasureCards() {
    this._edgeAnalyticsDataChangeSubscriptions.forEach(subscription => subscription.unsubscribe());
    this._edgeAnalyticsDataChangeSubscriptions = [];
    for (const [measureType, measure] of this._measures) {
      let metric = this.metrics.find(m => m.tool === measureType);
      if (!metric) {
        console.error('Metric not found for measure type:', measureType);
        continue;
      }
      try {
        switch (measureType) {
          case MeasureType.meeting:
            {
              const meetingMeasureData = measure as MeetingMeasureData;
              const systemUserDimensions = meetingMeasureData.dataStream._systemUserDimensions$.getValue();
              this._edgeAnalyticsDataCubeUserId = systemUserDimensions.find(u => u.internalemailaddress === this.authService.user.userPrincipalName)?.sk_systemuserid;
              const dataCubeSubscription = meetingMeasureData.dataStream._dataCube$.subscribe(this.updateEdgeAnalyticsMeetingMetric.bind(this));
              this._edgeAnalyticsDataChangeSubscriptions.push(dataCubeSubscription);
            }
            break;
          case MeasureType.message:
            {
              const messageMeasureData = measure as MessageMeasureData;
              const systemUserDimensions = messageMeasureData.dataStream._systemUserDimensions$.getValue();
              this._edgeAnalyticsDataCubeUserId = systemUserDimensions.find(u => u.internalemailaddress === this.authService.user.userPrincipalName)?.sk_systemuserid;
              const dataCubeSubscription = messageMeasureData.dataStream._dataCube$.subscribe(this.updateEdgeAnalyticsMessageMetric.bind(this));
              this._edgeAnalyticsDataChangeSubscriptions.push(dataCubeSubscription);
            }
            break;
          case MeasureType.coaching:
            {
              const coachingMeasureData = measure as CoachingMeasureData;
              const dataCubeSubscription = coachingMeasureData.dataStream.userFilteredCube$.subscribe(this.updateEdgeAnalyticsCoachingMetric.bind(this));
              this._edgeAnalyticsDataChangeSubscriptions.push(dataCubeSubscription);
            }
            break;
          case MeasureType.procedure:
            {
              const procedureMeasureData = measure as SurgeryOrderMeasureData;
              const dataCubeSubscription = procedureMeasureData.dataStream.allOrders$.subscribe(this.updateEdgeAnalyticsProcedureMetric.bind(this));
              this._edgeAnalyticsDataChangeSubscriptions.push(dataCubeSubscription);
            }
            break;
          case MeasureType.salesOrder:
            {
              let metric = this.metrics.find(m => m.tool === MeasureType.salesOrder);
              const actualTarget = (measure as any).indskr_acapyrd;
              metric.stringValue = (actualTarget || 0)
              metric.isLoading = false;
              this.cdr.markForCheck();
            }
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('Error updating edge analytics measure card:', error);
      }
      metric.isLoading = false;
    }
    this.cdr.markForCheck();
  }

  private updateEdgeAnalyticsMeetingMetric(dataCube: any) {
    let metric = this.metrics.find(m => m.tool === MeasureType.meeting);
    const statecode = dataCube.getDimensionMembers(MeetingDimensions.statecode).filter(stcd => stcd.statecode === 1);
    const products = dataCube.getDimensionMembers(MeetingDimensions.products);
    const users = dataCube.getDimensionMembers(CommonDimensions.users).filter(user => user.sk_systemuserid === this._edgeAnalyticsDataCubeUserId)
    const filters = {
      statecode,
      products,
      users
    }
    const trendData = this.getTrendData(dataCube, MeetingReportYearMonthKeyProp, (subCube) => uniqBy(subCube.getCells().filter(({ uniquerowid, sk_activityid }) => uniquerowid && sk_activityid), 'sk_activityid').length, filters);
    metric.data = trendData.data.map(data => ({
      value1: data.data,
      label: this._allMonths[data.month]
    }));
    metric.stringValue = `${trendData.summary || 0}`;
    metric.isLoading = false;
    this.cdr.markForCheck();
  }

  private updateEdgeAnalyticsMessageMetric(dataCube: any) {
    let metric = this.metrics.find(m => m.tool === MeasureType.message);
    const contacts = dataCube.getDimensionMembers(MessageDimensions.contacts).filter(m => m.sk_contactid);
    const channel = dataCube.getDimensionMembers(MessageDimensions.channel).filter(m => m.sk_channelid);
    const users = dataCube.getDimensionMembers(CommonDimensions.users).filter(u => u.sk_systemuserid === this._edgeAnalyticsDataCubeUserId);
    const filters = {
      contacts,
      channel,
      users
    }
    const trendData = this.getTrendData(dataCube, MessageReportYearMonthKeyProp, (subCube) => sumBy(subCube.getCells(), 'dm_sent'), filters);
    metric.data = trendData.data.map(data => ({
      // value1: data.data,
      value2: data.data,
      label: this._allMonths[data.month]
    }));
    metric.stringValue = `${trendData.summary || 0}`;
    metric.isLoading = false;
    this.cdr.markForCheck();
  }

  private updateEdgeAnalyticsCoachingMetric(dataCube: any) {
    let metric = this.metrics.find(m => m.tool === MeasureType.coaching);
    const filters = _getAvgCoachingScoreDiceFilter(dataCube, [], null, null);
    const trendData = this.getTrendData(dataCube, CoachingReportYearMonthKeyProp, (subCube) => sumBy(subCube.getCells(), 'coaching_score') / (subCube.getDimensionMembers(CoachingDimensions.activityId).length || 1), filters);
    metric.data = trendData.data.map(data => ({
      value2: data.data,
      label: this._allMonths[data.month]
    }));
    metric.stringValue = Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2, }).format(trendData.summary || 0);
    metric.isLoading = false;
    this.cdr.markForCheck();
  }

  private updateEdgeAnalyticsProcedureMetric(orders: SurgeryOrderActivity[]) {
    let metric = this.metrics.find(m => m.tool === MeasureType.procedure);
    const firstMonth = getYearMonth(3);
    const ordersByYearMonth = _(orders)
      .map(o => o.year * 100 + o.month)
      .filter(ym => ym >= firstMonth)
      .groupBy(ym => ym)
      .value();

    metric.data = _(range(3, -1, -1))
      .map(getYearMonth)
      .map(ym => ({month: (ym % 100) - 1, count: ordersByYearMonth[ym]?.length ?? 0}))
      .map(({month, count}) => {
        return {
          label: this._allMonths[month],
          value1: count
        }
      })
      .value();
    metric.stringValue = `${sumBy(metric.data, 'value1') || 0}`;
    metric.isLoading = false;
    this.cdr.markForCheck();
  }

  private getTrendData(cube: any, dimension: string, operation: (subCube: any) => number, filters: any) {
    try {
      const firstMonth = getYearMonth(3);
      if (!cube?.getDimensionMembers) {
        return {
          data: range(3, -1, -1).map(getYearMonth).map(yearMonth => ({
            month: (yearMonth % 100) - 1,
            data: 0
          })),
          summary: 0
        };
      }
      const dimensionMembers = cube.getDimensionMembers(CommonDimensions.yearMonth).filter(dm => dm[dimension] >= firstMonth);
      cube = cube.dice({
        ...filters || {},
        [CommonDimensions.yearMonth]: dimensionMembers
      });
      const dimensionMembersByYearMonth = keyBy(dimensionMembers, dimension);
      const last4MonthsData = range(3, -1, -1)
        .map(getYearMonth)
        .map(yearMonth => {
          const dimension = dimensionMembersByYearMonth[yearMonth];
          const month = (yearMonth % 100) - 1;
          if (!dimension) {
            console.error('Dimension not found for yearMonth:', yearMonth);
            return {
              month,
              data: 0
            }
          }
          const subCube = cube.slice(CommonDimensions.yearMonth, dimension);
          const data = operation(subCube);
          return {
            month,
            data
          }
        });
      return {
        data: last4MonthsData,
        summary: operation(cube)
      };
    } catch (error) {
      console.error('Error getting trend data:', error);
      return {
        data: [],
        summary: 0
      };
    }
    
  }

  private formatEstimatedValue(value: number): string {
    const roundTo1Decimal = (num: number) => {
      const temp = num * 100;
      const secondDecimal = Math.floor(temp) % 10;
      let rounded = Math.floor(num * 10) / 10;
      if (secondDecimal >= 5) {
        rounded = Math.ceil(num * 10) / 10;
      }
      return rounded.toFixed(1);
    };

    if (value >= 1_000_000_000) {
      return roundTo1Decimal(value / 1_000_000_000) + 'B';
    } else if (value >= 1_000_000) {
      return roundTo1Decimal(value / 1_000_000) + 'M';
    } else if (value >= 1000) {
      return roundTo1Decimal(value / 1000) + 'K';
    } else {
      return roundTo1Decimal(value);
    }
  }

  openTool(metric: Metric): void {
    if (this.isMetricDisabled(metric)) {
      this.notificationService.notify(
        this.translate.instant('METRIC_DATA_LOADING_PLEASE_WAIT'),
        'GoAppMetricsComponent',
        'top',
        ToastStyle.INFO,
      );
      return;
    }

    switch (metric.tool) {
      case 'call_plan':
        this.reportDataMgmService.setSelectedMeasure(null);
        if (this.authService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.ORG_CALLPLAN_GA_HIDE_CALL_PLAN_TOOL) === true) {
          this.events.publish('navigateTo', 'contacts');
          break;
        }
        if(this.isDetailPagePushed()) {
          this.navService.popChildNavPageWithPageTracking();
        }
        this.navService.pushWithPageTracking(PageName.CustomerCallPlanPage, PageName.CustomerCallPlanPage, { 'listMode': ComponentViewMode.LISTVIEW, __source: 'menu' }, PageName.CustomerCallPlanPage);
        break;
      case 'opportunity':
        if(this.isDetailPagePushed()) {
          this.navService.popChildNavPageWithPageTracking();
        }
        this.reportDataMgmService.setSelectedMeasure(null);
        this.navService.pushWithPageTracking(PageName.OpportunityManagementPage, PageName.OpportunityManagementPage, { 'listMode': ComponentViewMode.LISTVIEW, __source: 'menu' }, PageName.OpportunityManagementPage);
        break;
      case MeasureType.procedure:
        this.reportDataMgmService.resetLoadingCounter();
        this.reportDataMgmService.requestLocalDataRefresh(MeasureType.procedure);
      case MeasureType.meeting:
      case MeasureType.message:
      case MeasureType.coaching:
      case MeasureType.salesOrder:
        this.openSelectedMeasureDetail(metric.tool);
        break;
      default:
        console.error('Unknown tool:', metric.tool);
        break;
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  private async openSelectedMeasureDetail(measureType: MeasureType) {
    const curSelectedMeasure = this.reportDataMgmService.getSelectedMeasure();
    if (curSelectedMeasure && measureType === curSelectedMeasure) {
      return;
    }
    const curSelectedMeasureCard = curSelectedMeasure && this.metrics.find(m => m.tool === curSelectedMeasure);
    if (curSelectedMeasureCard) {
      curSelectedMeasureCard.isSelected = false;
    }

    const newSelectedMeasureCard = this.metrics.find(m => m.tool === measureType);
    if (!newSelectedMeasureCard) {
      return;
    }
    newSelectedMeasureCard.isSelected = true;
    this.reportDataMgmService.setSelectedMeasure(measureType);

    // Overwrite data updated time to current time as we're using local data for certain types
    if (measureType === MeasureType.procedure) {
      this.reportService.overwriteMeasureUpdateTime();
    }

    const isDetailPagePushed = this.isDetailPagePushed();
    if (!isDetailPagePushed) {
      const isRightPaneShown = this.uiService.showRightPane;
      this.uiService.showRightPane = this.uiService.showRightPaneOnly = true;
      this.navService.pushChildNavPageWithPageTracking(PageName.EdgeAnalyticsDetailComponent, PageName.EdgeAnalyticsDetailComponent, PageName.GoAppActivitiesPageComponent, { from: 'GoAppHomeComponent', rightPaneShownBeforeMeasureToolOpen: isRightPaneShown });
    }
    this.trackMeasureClickEvent(measureType);
  }

  private isDetailPagePushed() {
    return (this.navService.getActiveChildNavViewPageName() !== undefined) && (this.navService.getActiveChildNavViewPageName() === PageName.EdgeAnalyticsDetailComponent);
  }

  private trackMeasureClickEvent(measureType: MeasureType) {
    switch (measureType) {
      case MeasureType.meeting:
        this.trackService.tracking('EdgeMeetingsClick', TrackingEventNames.EDGE_ANALYTICS);
        break;
      case MeasureType.message:
        this.trackService.tracking('EdgeMessagesClick', TrackingEventNames.EDGE_ANALYTICS);
        break;
      case MeasureType.coaching:
        this.trackService.tracking('EdgeCoachingsClick', TrackingEventNames.EDGE_ANALYTICS);
        break;
      case MeasureType.procedure:
        this.trackService.tracking('EdgeProceduresClick', TrackingEventNames.EDGE_ANALYTICS);
        break;
      default:
        break;
    }
  }
  @HostListener('mousedown', ['$event'])
  @HostListener('touchstart', ['$event'])
  onMouseDown(event: MouseEvent | TouchEvent) {
    if (event instanceof TouchEvent && 'ontouchstart' in window && navigator.maxTouchPoints > 0) {
      return;
    }
    const startX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const scrollContainer = this.el.nativeElement;
    if (!scrollContainer) return;

    const initialScrollLeft = scrollContainer.scrollLeft;
    let dragged = false;

    function onMouseMove(moveEvent: MouseEvent | TouchEvent) {
      const currentX = moveEvent instanceof MouseEvent ? moveEvent.clientX : moveEvent.touches[0].clientX;
      const diff = currentX - startX;

      if (Math.abs(diff) > 5) {
        dragged = true;
      }

      scrollContainer.scrollLeft = initialScrollLeft - diff;
    }

    function onMouseUp(upEvent: Event) {
      bindEvents(false);

      // Prevent accidental click after drag
      if (dragged) {
        const preventClick = (clickEvent: Event) => {
          clickEvent.stopPropagation();
          clickEvent.preventDefault();
          scrollContainer.removeEventListener('click', preventClick, true);
        };
        scrollContainer.addEventListener('click', preventClick, true);
      }
    }

    function bindEvents(add: boolean) {
      const fn = add ? document.addEventListener : document.removeEventListener;
      fn('mousemove', onMouseMove);
      fn('mouseup', onMouseUp);
      fn('mouseleave', onMouseUp);
      fn('touchmove', onMouseMove);
      fn('touchend', onMouseUp);
    }

    bindEvents(true);
  }

  trackById(index: number, item: any) { return item?.id || item?.ID || index; }
}
